import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Star, ArrowLeft, Check, Sparkles, TrendingUp, Eye, ShieldCheck,
    Loader2, CheckCircle2, AlertCircle, Copy, Zap, CreditCard, ChevronDown,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { startFeaturePayment } from "../lib/razorpay";

const PRICE = 199;
const DAYS = 30;
const UPI_ID = "globalpropertymart@upi";

const BENEFITS = [
    { icon: TrendingUp, title: "Top of Search", desc: "Your listing shows above regular properties for buyers browsing your city and locality." },
    { icon: Star, title: "Golden ⭐ Badge", desc: "A distinctive Featured badge grabs attention on every card view across the platform." },
    { icon: Eye, title: "6× More Views", desc: "Featured properties get on average 6× more views and 3× more enquiries than regular listings." },
    { icon: Sparkles, title: "Homepage Rotation", desc: "Included in the homepage 'Handpicked Featured' carousel seen by every visitor." },
    { icon: ShieldCheck, title: "Priority Support", desc: "Our team responds to your enquiries and support tickets first while featured." },
];

export default function FeatureProperty() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [property, setProperty] = useState(null);
    const [status, setStatus] = useState(null);
    const [utr, setUtr] = useState("");
    const [method, setMethod] = useState("upi");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [paying, setPaying] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/properties/${id}`).then((r) => setProperty(r.data)).catch(() => setProperty({ notFound: true }));
        api.get(`/properties/${id}/feature-status`).then((r) => setStatus(r.data)).catch(() => {});
    }, [id]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (utr.trim().length < 4) {
            setError("Please enter your UPI/UTR reference (min 4 characters).");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/properties/${id}/feature-request`, {
                payment_reference: utr.trim(),
                payment_method: method,
                note: note || null,
            });
            toast.success("Feature request submitted! Our team will verify payment shortly.");
            navigate("/my-properties");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Failed to submit request. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const payOnline = async () => {
        setError("");
        setPaying(true);
        try {
            const res = await startFeaturePayment(id);
            if (res.cancelled) return;
            if (res.ok) {
                toast.success(res.message || "Payment Successful. Your Featured Property request has been submitted for admin approval.");
                navigate("/my-properties");
            }
        } catch (err) {
            setError(formatApiError(err?.message) || "Payment failed. Please try again.");
        } finally {
            setPaying(false);
        }
    };

    const copyUpi = () => {
        navigator.clipboard.writeText(UPI_ID);
        toast.success("UPI ID copied");
    };

    if (property?.notFound) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-6">
                <div className="text-center">
                    <div className="text-navy-500/50 mb-3">Property not found</div>
                    <Link to="/my-properties" className="text-gold-700 underline">Back to My Properties</Link>
                </div>
            </div>
        );
    }

    // Already featured → block new request
    if (status?.active_request || status?.featured) {
        const expiresIso = status?.active_request?.expires_at || status?.featured_expires_at;
        return (
            <FeatureShell propertyTitle={property?.title}>
                <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
                        <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                    </div>
                    <h2 className="font-heading font-bold text-navy-500 text-xl mb-1">Already Featured ⭐</h2>
                    <p className="text-sm text-navy-500/60 max-w-md mx-auto">
                        This property is currently featured and will stay live until{" "}
                        <strong>{expiresIso ? new Date(expiresIso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "expiry"}</strong>.
                    </p>
                    <Link to="/my-properties" className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-gold-500 text-navy-500 text-sm font-extrabold hover:bg-gold-400">
                        Back to My Properties
                    </Link>
                </div>
            </FeatureShell>
        );
    }

    // Pending admin approval
    if (status?.pending_request) {
        return (
            <FeatureShell propertyTitle={property?.title}>
                <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-3">
                        <Loader2 className="w-9 h-9 text-amber-600 animate-spin" />
                    </div>
                    <h2 className="font-heading font-bold text-navy-500 text-xl mb-1">Awaiting Approval</h2>
                    <p className="text-sm text-navy-500/60 max-w-md mx-auto">
                        Your payment reference <strong className="text-navy-500">{status.pending_request.payment_reference}</strong> was received. Our team is verifying it — you&apos;ll be notified within 24 hours.
                    </p>
                    <Link to="/my-properties" className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-gold-500 text-navy-500 text-sm font-extrabold hover:bg-gold-400">
                        Back to My Properties
                    </Link>
                </div>
            </FeatureShell>
        );
    }

    return (
        <FeatureShell propertyTitle={property?.title}>
            {/* Price banner */}
            <div className="rounded-2xl bg-gradient-to-br from-gold-500 to-amber-500 text-navy-500 p-5 mb-5 relative overflow-hidden">
                <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10" />
                <div className="absolute right-6 bottom-2 w-16 h-16 rounded-full bg-white/10" />
                <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest font-extrabold opacity-70">Feature Your Property</div>
                    <div className="mt-1 flex items-baseline gap-1">
                        <span className="font-heading font-black text-4xl">₹{PRICE}</span>
                        <span className="font-bold text-sm opacity-70">/ {DAYS} days</span>
                    </div>
                    <div className="mt-2 text-sm font-semibold opacity-80">One-time payment · No auto-renewal</div>
                </div>
            </div>

            {/* Benefits */}
            <div className="mb-5">
                <div className="text-[11px] uppercase tracking-widest text-navy-500/50 font-bold mb-2">What you get</div>
                <ul className="space-y-2">
                    {BENEFITS.map((b) => {
                        const Icon = b.icon;
                        return (
                            <li key={b.title} className="flex items-start gap-3 rounded-lg bg-white border border-navy-500/8 p-3">
                                <span className="w-8 h-8 rounded-lg bg-gold-500/15 text-gold-700 flex items-center justify-center shrink-0">
                                    <Icon className="w-4 h-4" />
                                </span>
                                <div>
                                    <div className="text-sm font-bold text-navy-500 flex items-center gap-1.5">
                                        <Check className="w-3.5 h-3.5 text-emerald-600" /> {b.title}
                                    </div>
                                    <div className="text-[12px] text-navy-500/65 leading-relaxed mt-0.5">{b.desc}</div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Primary: Razorpay online payment */}
            <div className="rounded-xl bg-white border-2 border-gold-500/50 p-4 mb-4 shadow-lg shadow-gold-500/10">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gold-700 font-extrabold mb-1">
                    <CreditCard className="w-4 h-4" /> Instant · Secure · Recommended
                </div>
                <div className="text-sm text-navy-500/70 mb-3">
                    Pay ₹{PRICE} via UPI, Cards, Netbanking or Wallets. After successful payment, your request goes to admin for approval.
                </div>
                <button
                    type="button"
                    onClick={payOnline}
                    disabled={paying}
                    data-testid="feature-pay-razorpay-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-gradient-to-r from-gold-500 to-amber-500 text-navy-500 font-extrabold text-base hover:opacity-95 disabled:opacity-60 shadow-md"
                >
                    {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Star className="w-5 h-5" />}
                    {paying ? "Opening payment…" : `Pay ₹${PRICE} & Submit for Approval`}
                </button>
                <p className="text-[10px] text-navy-500/50 text-center mt-2">
                    100% secure · Powered by Razorpay · Your property becomes Featured only after admin approves.
                </p>
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 flex items-start gap-2 text-[13px] text-red-700 mb-3">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
                </div>
            )}

            {/* Secondary: Manual UPI (collapsible) */}
            <button
                type="button"
                onClick={() => setShowManual((v) => !v)}
                data-testid="feature-toggle-manual"
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-navy-500/15 bg-navy-500/5 text-sm font-semibold text-navy-500 hover:bg-navy-500/8"
            >
                <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-navy-500/60" /> Prefer direct UPI transfer? Submit UTR manually</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showManual ? "rotate-180" : ""}`} />
            </button>

            {showManual && (
                <div className="mt-3 rounded-xl bg-navy-500/5 border border-navy-500/15 p-4">
                    <div className="flex items-center gap-2 text-sm font-heading font-bold text-navy-500 mb-2">
                        <Zap className="w-4 h-4 text-gold-600" /> How to pay ₹{PRICE} manually
                    </div>
                    <ol className="space-y-2 text-[13px] text-navy-500/80 list-decimal list-inside">
                        <li>Pay <strong>₹{PRICE}</strong> to our UPI ID via GPay / PhonePe / Paytm / BHIM.</li>
                        <li>Copy the UPI reference (UTR) shown in your bank app after payment.</li>
                        <li>Paste it below and submit — we&apos;ll verify and activate within 24 hours.</li>
                    </ol>
                    <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-white border border-navy-500/10 px-3 py-2">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-navy-500/50 font-bold">Pay to UPI ID</div>
                            <div className="text-navy-500 font-heading font-bold text-sm">{UPI_ID}</div>
                        </div>
                        <button
                            type="button"
                            onClick={copyUpi}
                            data-testid="copy-upi-btn"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gold-500 text-navy-500 text-xs font-extrabold hover:bg-gold-400"
                        >
                            <Copy className="w-3.5 h-3.5" /> Copy
                        </button>
                    </div>
                    <form onSubmit={submit} className="mt-4 space-y-3">
                        <label className="block">
                            <div className="text-[11px] font-bold text-navy-500/70 mb-1.5">Payment method</div>
                            <div className="flex gap-2 flex-wrap">
                                {[
                                    { v: "upi", l: "UPI" },
                                    { v: "bank", l: "Bank transfer" },
                                    { v: "other", l: "Other" },
                                ].map((m) => (
                                    <button
                                        type="button"
                                        key={m.v}
                                        onClick={() => setMethod(m.v)}
                                        data-testid={`method-${m.v}`}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold border ${method === m.v ? "bg-gold-500 text-navy-500 border-gold-500" : "bg-white text-navy-500/70 border-navy-500/20 hover:border-gold-500/40"}`}
                                    >
                                        {m.l}
                                    </button>
                                ))}
                            </div>
                        </label>
                        <label className="block">
                            <div className="text-[11px] font-bold text-navy-500/70 mb-1.5">UTR / Transaction ID <span className="text-red-500">*</span></div>
                            <input
                                value={utr}
                                onChange={(e) => setUtr(e.target.value)}
                                data-testid="feature-utr-input"
                                placeholder="e.g. 328165492001"
                                className="w-full rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm text-navy-500 focus:outline-none focus:border-gold-500/60"
                            />
                        </label>
                        <label className="block">
                            <div className="text-[11px] font-bold text-navy-500/70 mb-1.5">Note (optional)</div>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={2}
                                data-testid="feature-note-input"
                                className="w-full rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm text-navy-500 focus:outline-none focus:border-gold-500/60 resize-none"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="feature-submit-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg border border-navy-500/25 text-navy-500 font-extrabold text-sm hover:bg-navy-500/5 disabled:opacity-60"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            {submitting ? "Submitting…" : "Submit manual request"}
                        </button>
                    </form>
                </div>
            )}
        </FeatureShell>
    );
}

function FeatureShell({ propertyTitle, children }) {
    return (
        <div className="min-h-[70vh] bg-cream-50" data-testid="feature-property-page">
            <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-4 pb-6">
                <div className="flex items-center gap-3">
                    <Link
                        to="/my-properties"
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0"
                        aria-label="Back"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-gold-400 font-bold">Feature Property</div>
                        <h1 className="font-heading font-bold text-lg truncate">{propertyTitle || "Your Property"}</h1>
                    </div>
                </div>
            </div>
            <div className="px-3 sm:px-6 max-w-2xl mx-auto -mt-4 pb-8">
                <div className="bg-white rounded-2xl shadow-sm border border-navy-500/8 p-4 sm:p-5">
                    {children}
                </div>
            </div>
        </div>
    );
}
